import { useState } from "react"
import { router, useForm, usePage } from "@inertiajs/react"
import { AlertCircle } from "lucide-react"

import Layout from "@/pages/layouts/application"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import PostCreationBlock from "@/pages/organizations/posts/components/post_creation_block"
import { useThread } from "@/pages/organizations/posts/hooks/thread"
import * as api from "@/api"

export default function Show({ assets = [] }) {
  const page = usePage()
  const { user, organization } = page.props.auth
  const { integrations } = page.props
  const { url } = page
  const { twitter, linkedin, readwise, slack, calendar } = integrations

  const {
    data: postData,
    setData: setPostData,
    post: submitPost,
    transform: transformPost,
    processing: postProcessing,
    errors: postErrors,
  } = useForm({
    scheduled_at: null,
    file: null,
    asset_id: null,
    content: "",
  })

  // Separate thread state for each platform
  const { 
    posts: twitterPosts, 
    updatePost: updateTwitterPost, 
    addPost: addTwitterPost, 
    removePost: removeTwitterPost, 
    joinedContent: twitterJoinedContent 
  } = useThread("")
  
  const { 
    posts: linkedinPosts, 
    updatePost: updateLinkedinPost, 
    addPost: addLinkedinPost, 
    removePost: removeLinkedinPost, 
    joinedContent: linkedinJoinedContent 
  } = useThread("")

  const [crossPostEnabled, setCrossPostEnabled] = useState(true)
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState(null)
  const [mediaType, setMediaType] = useState(null);
  const [replyContent, setReplyContent] = useState("")

  const handleReplyChange = (content) => {
    setReplyContent(content)
  }

  // Wrapper functions that sync both platforms when cross-posting is enabled
  const handleUpdateTwitterPost = (index, content) => {
    updateTwitterPost(index, content)
    if (crossPostEnabled) {
      updateLinkedinPost(index, content)
    }
  }

  const handleUpdateLinkedinPost = (index, content) => {
    updateLinkedinPost(index, content)
    if (crossPostEnabled) {
      updateTwitterPost(index, content)
    }
  }

  const handleAddTwitterPost = () => {
    addTwitterPost()
    if (crossPostEnabled) {
      addLinkedinPost()
    }
  }

  const handleAddLinkedinPost = () => {
    addLinkedinPost()
    if (crossPostEnabled) {
      addTwitterPost()
    }
  }

  const handleRemoveTwitterPost = (index) => {
    removeTwitterPost(index)
    if (crossPostEnabled) {
      removeLinkedinPost(index)
    }
  }

  const handleRemoveLinkedinPost = (index) => {
    removeLinkedinPost(index)
    if (crossPostEnabled) {
      removeTwitterPost(index)
    }
  }

  const handlePostSubmit = (event, selectedTargets) => {
    event.preventDefault()

    // Redirect to upgrade page if no free posts remaining
    if (!organization.subscribed && organization.free_posts_remaining <= 0) {
      router.visit(api.organizationsUpgrade.show.path({ org_nano_id: organization.nano_id }))
      return
    }

    const postPayload = {
      scheduled_at: postData.scheduled_at,
      twitter_content: twitterJoinedContent,
      linkedin_content: linkedinJoinedContent,
      targets: selectedTargets,
    }

    if (selectedTargets.includes("linkedin") && replyContent.trim()) {
      postPayload.reply_attributes = {
        content: replyContent.trim(),
      }
    }

    if (postData.file) {
      postPayload.asset_attributes = { file: postData.file }
    } else if (postData.asset_id) {
      postPayload.asset_attachment_attributes = { asset_id: postData.asset_id }
    }

    router.post(api.organizationsPosts.create.path({ org_nano_id: organization.nano_id }), {
      post: postPayload
    }, {
      preserveScroll: true,
    })
  }

  const handlePublishNow = (e, selectedTargets) => {
    e.preventDefault()

    // Redirect to upgrade page if no free posts remaining
    if (!organization.subscribed && organization.free_posts_remaining <= 0) {
      router.visit(api.organizationsUpgrade.show.path({ org_nano_id: organization.nano_id }))
      return
    }

    const postPayload = {
      scheduled_at: new Date().toISOString(),
      twitter_content: twitterJoinedContent,
      linkedin_content: linkedinJoinedContent,
      targets: selectedTargets,
      publish_now: true
    }

    if (selectedTargets.includes("linkedin") && replyContent.trim()) {
      postPayload.reply_attributes = {
        content: replyContent.trim(),
      }
    }

    if (postData.file) {
      postPayload.asset_attributes = { file: postData.file }
    } else if (postData.asset_id) {
      postPayload.asset_attachment_attributes = { asset_id: postData.asset_id }
    }

    router.post(api.organizationsPosts.create.path({ org_nano_id: organization.nano_id }), {
      post: postPayload
    }, {
      preserveScroll: true,
    })
  }

  const handleMediaChange = (mediaData, previewUrl, mediaType) => {
    if (mediaData?.file) {
      setPostData({ ...postData, file: mediaData.file, asset_id: null })
    } else if (mediaData?.asset_id) {
      setPostData({ ...postData, asset_id: mediaData.asset_id, file: null })
    } else {
      setPostData({ ...postData, file: null, asset_id: null })
    }
    setMediaPreviewUrl(previewUrl)
    setMediaType(mediaType)
  }

  const handleNewDraft = () => {
    // Clear Twitter posts
    for (let i = twitterPosts.length - 1; i > 0; i -= 1) {
      removeTwitterPost(i)
    }
    updateTwitterPost(0, "")
    // Clear LinkedIn posts
    for (let i = linkedinPosts.length - 1; i > 0; i -= 1) {
      removeLinkedinPost(i)
    }
    updateLinkedinPost(0, "")
    setPostData({ ...postData, file: null, asset_id: null })
    setMediaPreviewUrl(null)
    setMediaType(null)
    setReplyContent("")
  }

  const isSubmitDisabled = postProcessing ||
    !postData.scheduled_at ||
    (twitterPosts.every((post) => !post.trim()) && linkedinPosts.every((post) => !post.trim()))

  const currentSelection = postData.file
    ? { type: "new", url: mediaPreviewUrl, mediaType }
    : postData.asset_id
    ? { type: "existing", id: postData.asset_id, url: mediaPreviewUrl, mediaType }
    : null

  return (
    <Layout>
      <div className="container mx-auto p-6 flex flex-col gap-6">
        {!organization.subscribed && organization.free_posts_remaining <= 0 && (
          <Alert className="bg-secondary">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Upgrade to continue posting</AlertTitle>
            <AlertDescription>
              You've used all your free posts. Upgrade to schedule unlimited posts.
            </AlertDescription>
          </Alert>
        )}

        <PostCreationBlock
          scheduledAt={postData.scheduled_at}
          onScheduleChange={(newDate) => setPostData("scheduled_at", newDate)}
          onSubmit={handlePostSubmit}
          onPublishNow={handlePublishNow}
          onNewDraft={handleNewDraft}
          isSubmitDisabled={isSubmitDisabled}
          errors={postErrors}
          twitterPosts={twitterPosts}
          onUpdateTwitterPost={handleUpdateTwitterPost}
          onAddTwitterPost={handleAddTwitterPost}
          onRemoveTwitterPost={handleRemoveTwitterPost}
          linkedinPosts={linkedinPosts}
          onUpdateLinkedinPost={handleUpdateLinkedinPost}
          onAddLinkedinPost={handleAddLinkedinPost}
          onRemoveLinkedinPost={handleRemoveLinkedinPost}
          crossPostEnabled={crossPostEnabled}
          onCrossPostToggle={() => setCrossPostEnabled(prev => !prev)}
          assets={assets}
          onMediaChange={handleMediaChange}
          mediaSelection={currentSelection}
          organizationName={organization?.name}
          userEmail={user?.email}
          integrations={integrations}
          onReplyChange={handleReplyChange}
          organizationId={organization?.id}
        />
      </div>
    </Layout>
  )
}

